import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource} from '@api'
import {SplitterNode, TabNode, TabSetNode} from '@flexlayout'
import {DefaultCatalogContextMenuItems, FlexLayoutContextMenu, IContextMenuItems, StoresInstanceContext, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {IFlexLayoutTabProps, StoresInstance} from '@stores'
import {IFlexLayoutCatalogRootConfig} from '@stores/catalog/flexLayout'
import {autorun, runInAction} from 'mobx'
import * as css from './VipFlexLayoutTab.css'

/// region Store

export class VipFlexLayoutTabStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, public props: Exclude<MyProps, 'className'>) {
    super(stores)

    if (this.tab) {
      this.tab.vipTabStore = this
    }
  }

  @computed get extraData() {
    return this.tab.extraData
  }

  @computed get storesToInject() : StoresInstance {
    const {tab} = this
    return !tab ? this.stores : tab.extraData?.store?.overriddenStores ?? tab.catalogRoot?.stores ?? this.stores
  }

  @computed get toolWindowStoresToInject() {
    const {tab: {component, extraData}} = this
    if (component === 'inlineCatalogItemDetails') {
      return extraData?.store?.parentTab?.storesToInject ?? this.storesToInject
    }
    return this.storesToInject
  }

  registerEffects = () => {
    const toDispose: Array<IDisposer> = [
      autorun(() => {
        const {storesToInject, tab} = this
        if (storesToInject?.vipTabs && tab?.component) {
          storesToInject.vipTabs[tab.component] = this
        }
      })
    ]

    return () => {
      const {tab} = this
      if (tab?.vipTabStore == this) { tab.vipTabStore = null }
      // console.log(`Unmount vip tab ${this.tab.id} - ${this.tab.name}`)
      toDispose.forEach(d => d())
    }
  }

  onContextMenu = e => {
    const {contextMenu, node} = this
    contextMenu.onComponentContextMenu(e, this, {node, vipStore: this})
  }

  @computed get store() {
    const {tab: {extraData: {store}}} = this
    return this.tab?.extraData?.store
  }

  @computed get contextMenuItems() {
    const {store, flexLayout} = this

    return <StoresInstanceContext.Provider value={store?.overriddenStores ?? store?.stores}>
      <VipFlexLayoutTabStoreContext.Provider value={this}>
        {flexLayout.contextMenuItems}
      </VipFlexLayoutTabStoreContext.Provider>
    </StoresInstanceContext.Provider>
  }

  get flexLayout() { return this.stores.flexLayout}

 @computed get node() {
    const {flexLayout: {model}, props: {flexNode}} = this

    if (!flexNode || !model) return null

    return model.nodeById(flexNode.id) ?? flexNode
  }

  @computed get tab(): TabNode { return this.node instanceof TabNode ? this.node : null}

  @computed get splitter() : SplitterNode { return this.node instanceof SplitterNode ? this.node : null}
  /**
   * Don't make computed as you can't trust prior values
   */
  get tabSet(): TabSetNode {
    const {node} = this

    if (node instanceof TabSetNode) return node

    const parent = node?.parent
    return parent?.attributes?.type === 'tabset'
           ? parent as TabSetNode
           : null
  }

  get config() {
    return (this.tab?.attributes?.config as IFlexLayoutCatalogRootConfig)
  }

  @computed get tabToMyRight() {
    const {tab, tabSet} = this

    if (!tab || !tabSet) return null

    const {indexInParent} = tab
    if (!tabSet || indexInParent + 1 >= tabSet.children.length) return null

    return tabSet.children[indexInParent + 1] as TabNode
  }

  @computed get tabsToMyRight(): Array<TabNode> {
    const {tab, tabSet} = this

    if (!tab || !tabSet) return null

    const {indexInParent} = tab
    if (!tabSet || indexInParent + 1 >= tabSet.children.length) return null

    return tabSet.children.slice(indexInParent + 1) as Array<TabNode>
  }

  @computed get tabsToMyLeft(): Array<TabNode> {
    const {tab, tabSet} = this

    if (!tab || !tabSet) return null

    const {indexInParent} = tab
    if (!tabSet || indexInParent === 0) return null

    return tabSet.children.slice(0, indexInParent - 1) as Array<TabNode>
  }
}

export const VipFlexLayoutTabStoreContext = React.createContext<VipFlexLayoutTabStore>(null)
export const useVipFlexLayoutTabStore = () => {
  let r = React.useContext(VipFlexLayoutTabStoreContext),
    stores = useStores()

  return r ?? stores?.catalogTab?.tab?.vipTabStore
}

/// endregion

/// region Components

interface MyProps extends IFlexLayoutTabProps {
  className?: string
}

export const VipFlexLayoutTab = observer<MyProps>(function VipFlexLayoutTab({className, children, ...props}) {
  const obsProps = useAsObservableSource({ isLayoutPreview: props.isLayoutPreview, flexNode: props.flexNode}),
    store = useVipLocalStore((stores) => new VipFlexLayoutTabStore(stores, obsProps)),
    {onContextMenu, storesToInject} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <VipFlexLayoutTabStoreContext.Provider value={store}>
      <StoresInstanceContext.Provider value={storesToInject}>
        <div className={classNames(css.root, className)} onContextMenu={onContextMenu}>
          {children}
        </div>
      </StoresInstanceContext.Provider>
    </VipFlexLayoutTabStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/VipFlexLayoutTab.tsx